import type { FieldErrors } from '../validation/fieldError';

/**
 * Jerarquía única de errores de la aplicación.
 *
 * No contiene textos visibles a propósito: la traducción a mensaje ocurre en la capa de UI
 * (`ui/components/errorPresentation`), de modo que el dominio se puede probar sin renderizar y
 * los textos se pueden localizar.
 */
export type AppError =
  /** No hay red en el dispositivo. Distinto de `SERVER`: aquí se ofrece el contenido en caché. */
  | { readonly kind: 'OFFLINE' }
  /** Hubo red pero la petición falló a nivel de transporte. */
  | { readonly kind: 'NETWORK'; readonly cause: string | null }
  /** La petición excedió el tiempo máximo. Se distingue de `NETWORK` porque reintentar suele bastar. */
  | { readonly kind: 'TIMEOUT' }
  /** 5xx. Reintentable con backoff exponencial. */
  | { readonly kind: 'SERVER'; readonly code: number }
  /** 404: el recurso no existe. Se presenta como estado vacío, no como error alarmante. */
  | { readonly kind: 'NOT_FOUND' }
  /** 401: token ausente, inválido o expirado. Obliga a re-autenticar preservando el borrador. */
  | { readonly kind: 'SESSION_EXPIRED' }
  | { readonly kind: 'FORBIDDEN' }
  /**
   * 409: la mesa fue tomada por otra persona entre la consulta y la confirmación.
   * **Nunca se reintenta automáticamente**: reintentar no la devuelve.
   */
  | { readonly kind: 'SLOT_TAKEN' }
  /** 422: el servidor rechazó campos concretos. Se pinta bajo cada campo, no en un aviso global. */
  | { readonly kind: 'VALIDATION'; readonly fields: FieldErrors }
  | { readonly kind: 'RATE_LIMITED'; readonly retryAfterSeconds: number | null }
  /** Cualquier otra cosa, incluido un cuerpo de respuesta que no se pudo interpretar. */
  | { readonly kind: 'UNKNOWN'; readonly cause: string | null };

export const AppErrors = {
  offline: (): AppError => ({ kind: 'OFFLINE' }),
  network: (cause: string | null = null): AppError => ({ kind: 'NETWORK', cause }),
  timeout: (): AppError => ({ kind: 'TIMEOUT' }),
  server: (code: number): AppError => ({ kind: 'SERVER', code }),
  notFound: (): AppError => ({ kind: 'NOT_FOUND' }),
  sessionExpired: (): AppError => ({ kind: 'SESSION_EXPIRED' }),
  forbidden: (): AppError => ({ kind: 'FORBIDDEN' }),
  slotTaken: (): AppError => ({ kind: 'SLOT_TAKEN' }),
  validation: (fields: FieldErrors): AppError => ({ kind: 'VALIDATION', fields }),
  rateLimited: (retryAfterSeconds: number | null = null): AppError => ({
    kind: 'RATE_LIMITED',
    retryAfterSeconds,
  }),
  unknown: (cause: string | null = null): AppError => ({ kind: 'UNKNOWN', cause }),
};

/**
 * Reintentar solo tiene sentido cuando el mismo intento puede dar otro resultado.
 * Un 409 o un 422 dan siempre lo mismo: ahí la acción correcta es cambiar algo, no repetir.
 */
export function isRetryable(error: AppError): boolean {
  switch (error.kind) {
    case 'NETWORK':
    case 'TIMEOUT':
    case 'SERVER':
    case 'OFFLINE':
    case 'RATE_LIMITED':
      return true;
    default:
      return false;
  }
}
